// frontend/src/pages/dashboard/AgentDashboard.js
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Badge from '../../components/ui/Badge';

// =================== HELPERS ===================
function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

function formatRelativeTime(date) {
    if (!date) return '';
    const diffMs = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
}

function useCountUp(target, duration = 900) {
    const [value, setValue] = useState(0);
    const rafRef = useRef(null);
    const startRef = useRef(null);

    useEffect(() => {
        const num = Number(target) || 0;
        if (num === 0) { setValue(0); return; }
        const start = performance.now();
        startRef.current = start;
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(num * eased));
            if (p < 1) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target, duration]);

    return value;
}

// =================== MAIN ===================
export default function AgentDashboard() {
    const { user } = useSelector((state) => state.auth);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [isConnected, setIsConnected] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [period, setPeriod] = useState('month');
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [showCommandPalette, setShowCommandPalette] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [pulseKey, setPulseKey] = useState(0);

    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const lastActivityRef = useRef(Date.now());

    // -------- Toast helper --------
    const pushToast = useCallback((toast) => {
        const id = Date.now() + Math.random();
        setToasts((prev) => [...prev, { ...toast, id }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 5000);
    }, []);

    // -------- Queries --------
    const { data: stats, isLoading: statsLoading, refetch: refetchStats, error: statsError } = useQuery(
        ['agent-stats', period],
        () => api.get(`/dashboard/stats?period=${period}`).then((res) => res.data),
        { refetchOnWindowFocus: false, staleTime: 30000, retry: 2, retryDelay: 1000 }
    );

    const { data: recentQuotes, isLoading: quotesLoading } = useQuery(
        'agent-recent-quotes',
        () => api.get('/quotes?limit=5').then((res) => res.data),
        { refetchOnWindowFocus: false, staleTime: 30000, retry: 1 }
    );

    const { data: recentPolicies, isLoading: policiesLoading } = useQuery(
        'agent-recent-policies',
        () => api.get('/policies?limit=5').then((res) => res.data),
        { refetchOnWindowFocus: false, staleTime: 30000, retry: 1 }
    );

    const { data: recentCertificates, isLoading: certificatesLoading } = useQuery(
        'agent-recent-certificates',
        () => api.get('/cargo/certificates').then((res) => res.data),
        { refetchOnWindowFocus: false, staleTime: 30000, retry: 1 }
    );

    // -------- Keyboard shortcuts --------
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setShowCommandPalette(true);
            }
            if (e.key === 'Escape') setShowCommandPalette(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // -------- Idle auto-refresh --------
    useEffect(() => {
        const bump = () => { lastActivityRef.current = Date.now(); };
        ['mousemove', 'keydown', 'click', 'scroll'].forEach((ev) => window.addEventListener(ev, bump, { passive: true }));

        const id = setInterval(() => {
            const idleMs = Date.now() - lastActivityRef.current;
            if (idleMs > 60000) refetchStats();
        }, 30000);

        return () => {
            ['mousemove', 'keydown', 'click', 'scroll'].forEach((ev) => window.removeEventListener(ev, bump));
            clearInterval(id);
        };
    }, [refetchStats]);

    // -------- SSE --------
    const connectSSE = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        try {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.0.101:5038/api/v1';
            const url = `${baseUrl}/dashboard/realtime?token=${encodeURIComponent(token)}`;
            const eventSource = new EventSource(url);

            eventSource.onopen = () => {
                if (!isMountedRef.current) return;
                setIsConnected(true);
                setReconnectAttempts(0);
                setLastUpdated(new Date());
            };

            eventSource.onmessage = (event) => {
                if (!isMountedRef.current) return;
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'stats_update' || data.type === 'connected') {
                        refetchStats();
                        setLastUpdated(new Date());
                        setPulseKey((k) => k + 1);
                    } else if (data.type === 'activity_update') {
                        queryClient.invalidateQueries('agent-stats');
                        queryClient.invalidateQueries('agent-recent-quotes');
                        queryClient.invalidateQueries('agent-recent-policies');
                        queryClient.invalidateQueries('agent-recent-certificates');
                        pushToast({
                            icon: '✨',
                            title: 'New activity',
                            message: data.message || 'Your dashboard has been updated',
                            tone: 'info',
                        });
                    }
                } catch (err) {
                    console.error('[Dashboard SSE] Parse error:', err);
                }
            };

            eventSource.onerror = () => {
                if (!isMountedRef.current) return;
                setIsConnected(false);
                if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
                const delay = Math.min(5000 * Math.pow(1.5, reconnectAttempts), 30000);
                reconnectTimeoutRef.current = setTimeout(() => {
                    if (!isMountedRef.current) return;
                    setReconnectAttempts((p) => p + 1);
                    connectSSE();
                }, delay);
            };

            eventSourceRef.current = eventSource;
        } catch (err) {
            console.error('[Dashboard SSE] Connection error:', err);
            setIsConnected(false);
        }
    }, [refetchStats, queryClient, reconnectAttempts, pushToast]);

    useEffect(() => {
        isMountedRef.current = true;
        connectSSE();
        return () => {
            isMountedRef.current = false;
            if (eventSourceRef.current) eventSourceRef.current.close();
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        };
    }, [connectSSE]);

    // Polling fallback
    useEffect(() => {
        let pollInterval;
        if (!isConnected) pollInterval = setInterval(() => refetchStats(), 30000);
        return () => { if (pollInterval) clearInterval(pollInterval); };
    }, [isConnected, refetchStats]);

    // -------- Derived --------
    const statsData = useMemo(() => stats?.data || stats || {}, [stats]);
    const totalQuotes = statsData.totalQuotes || 0;
    const policies = statsData.policies || 0;
    const commissions = statsData.commissions || 0;
    const conversionRate = statsData.conversionRate || 0;
    const growthRate = statsData.growthRate || 0;
    const pendingQuotes = statsData.pendingQuotes || 0;
    const certificateCount = recentCertificates?.length || 0;

    // Unified activity feed
    const activityFeed = useMemo(() => {
        const items = [];
        (recentQuotes || []).slice(0, 3).forEach((q) => items.push({
            id: `q-${q.id}`, type: 'quote', icon: '📋',
            title: `Quote ${q.quote_number}`,
            subtitle: q.vessel_name || 'N/A',
            meta: `KES ${Number(q.total_amount || 0).toLocaleString()}`,
            status: q.status, date: q.created_at, link: `/quotes/${q.id}`,
            color: 'blue',
        }));
        (recentCertificates || []).slice(0, 3).forEach((c) => items.push({
            id: `c-${c.id}`, type: 'certificate', icon: '📄',
            title: c.certificate_no || 'Certificate',
            subtitle: c.idf_number || 'N/A',
            meta: c.cancelled_at ? 'Cancelled' : 'Active',
            status: c.cancelled_at ? 'cancelled' : 'issued',
            date: c.certificate_date || c.created_at,
            link: `/cargo/certificates/${c.id}`,
            color: c.cancelled_at ? 'red' : 'green',
        }));
        (recentPolicies || []).slice(0, 3).forEach((p) => items.push({
            id: `p-${p.id}`, type: 'policy', icon: '🛡️',
            title: p.policy_number,
            subtitle: p.vessel_name || 'N/A',
            meta: `KES ${Number(p.sum_insured || 0).toLocaleString()}`,
            status: p.payment_status || 'active',
            date: p.created_at, link: `/policies/${p.id}`,
            color: 'purple',
        }));
        return items.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 6);
    }, [recentQuotes, recentCertificates, recentPolicies]);

    // -------- Error --------
    if (statsError) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-8">
                <ErrorCard error={statsError} onRetry={() => refetchStats()} />
            </div>
        );
    }

    if (statsLoading) return <SkeletonLoader />;

    return (
        <div className="relative max-w-7xl mx-auto px-4 py-8 space-y-8 animate-fadeIn">
            {/* Ambient gradient mesh */}
            <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#3a663f]/5 dark:bg-[#3a663f]/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/5 dark:bg-blue-500/10 rounded-full blur-3xl" />
            </div>

            {/* ============ STATUS BAR ============ */}
            <StatusBar
                isConnected={isConnected}
                lastUpdated={lastUpdated}
                reconnectAttempts={reconnectAttempts}
                onRefresh={() => refetchStats()}
                period={period}
                setPeriod={setPeriod}
                pulseKey={pulseKey}
            />

            {/* ============ HERO ============ */}
            <HeroBanner user={user} stats={{ totalQuotes, policies, commissions, growthRate }} />

            {/* ============ QUICK ACTIONS ============ */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <QuickAction to="/quotes/new" icon="➕" label="New Quote" color="blue" />
                <QuickAction to="/cargo" icon="📄" label="Cargo Certificate" color="green" />
                <QuickAction to="/cargo/certificates" icon="📁" label="My Certificates" badge={certificateCount} color="purple" />
                <QuickAction
                    icon="⌘"
                    label="Command"
                    onClick={() => setShowCommandPalette(true)}
                    color="slate"
                />
            </div>

            {/* ============ STAT CARDS ============ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCard
                    icon="📋"
                    label="Total Quotes"
                    value={totalQuotes}
                    subValue={`${pendingQuotes} pending`}
                    gradient="from-[#2d5232] to-[#3a663f]"
                    accent="#3a663f"
                    sparkline={[3, 5, 4, 7, 6, 9, 8, 11]}
                    trend={growthRate}
                />
                <StatCard
                    icon="✅"
                    label="Active Policies"
                    value={policies}
                    subValue={`${Math.round((policies / (totalQuotes || 1)) * 100)}% conversion`}
                    gradient="from-[#3a663f] to-[#4d8c54]"
                    accent="#4d8c54"
                    sparkline={[1, 2, 3, 3, 4, 5, 6, 7]}
                />
                <StatCard
                    icon="💰"
                    label="Commissions"
                    value={commissions}
                    prefix="KES "
                    gradient="from-[#4d8c54] to-[#5fa06a]"
                    accent="#5fa06a"
                    sparkline={[2, 3, 3, 5, 4, 6, 7, 8]}
                />
                <StatCard
                    icon="📈"
                    label="Conversion Rate"
                    value={conversionRate}
                    suffix="%"
                    subValue={`${growthRate > 0 ? '+' : ''}${growthRate}% vs last month`}
                    gradient="from-[#5fa06a] to-[#7ab885]"
                    accent="#7ab885"
                    trend={growthRate}
                    sparkline={[4, 5, 6, 5, 7, 8, 9, 10]}
                />
            </div>

            {/* ============ MAIN GRID ============ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Activity Feed — 2 cols */}
                <div className="lg:col-span-2">
                    <ActivityFeed items={activityFeed} loading={quotesLoading || certificatesLoading || policiesLoading} />
                </div>

                {/* Side panel — 1 col */}
                <div className="space-y-6">
                    <GoalRing value={conversionRate} target={80} />
                    <QuickStats
                        certificates={certificateCount}
                        pendingQuotes={pendingQuotes}
                        policies={policies}
                    />
                </div>
            </div>

            {/* Recent policies full width */}
            <ActivityPanel
                title="Recent Policies"
                to="/policies"
                loading={policiesLoading}
                isEmpty={!recentPolicies || recentPolicies.length === 0}
                emptyMessage="No policies issued yet. Convert a quote to get started!"
                emptyLink="/quotes/new"
                emptyLinkText="Create Quote"
            >
                {recentPolicies?.slice(0, 5).map((p) => <PolicyItem key={p.id} policy={p} />)}
            </ActivityPanel>

            {/* Command palette */}
            {showCommandPalette && (
                <CommandPalette
                    onClose={() => setShowCommandPalette(false)}
                    navigate={navigate}
                />
            )}

            {/* Toasts */}
            <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
        </div>
    );
}

// =================== STATUS BAR ===================
function StatusBar({ isConnected, lastUpdated, reconnectAttempts, onRefresh, period, setPeriod, pulseKey }) {
    return (
        <div className="sticky top-2 z-30 flex items-center justify-between bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl rounded-2xl px-5 py-3 border border-white/40 dark:border-slate-700/60 shadow-lg shadow-black/5">
            <div className="flex items-center gap-3">
                <span className="relative flex h-3 w-3">
                    {isConnected && (
                        <span key={pulseKey} className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                    )}
                    <span className={`relative inline-flex rounded-full h-3 w-3 ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`} />
                </span>
                <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                    {isConnected ? 'Live' : 'Polling'}
                </span>
                {lastUpdated && (
                    <span className="hidden sm:inline text-xs text-gray-400 dark:text-slate-500">
                        • {formatRelativeTime(lastUpdated)}
                    </span>
                )}
                {reconnectAttempts > 0 && !isConnected && (
                    <span className="text-xs text-amber-500">• Retry #{reconnectAttempts}</span>
                )}
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={onRefresh}
                    className="p-2 text-gray-400 hover:text-[#3a663f] dark:hover:text-green-400 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#3a663f]/40"
                    title="Refresh data"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                </button>
                <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5">
                    {['day', 'week', 'month', 'year'].map((p) => (
                        <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-[#3a663f]/40 ${period === p
                                    ? 'bg-white dark:bg-slate-600 text-[#3a663f] dark:text-green-400 shadow-sm'
                                    : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white'
                                }`}
                        >
                            {p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

// =================== HERO ===================
function HeroBanner({ user, stats }) {
    const { totalQuotes, policies, commissions, growthRate } = stats;
    return (
        <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-3xl p-8 md:p-10 text-white overflow-hidden shadow-2xl group">
            {/* Animated background orbs */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3 group-hover:translate-x-1/4 transition-transform duration-1000" />
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/4 group-hover:-translate-x-1/3 transition-transform duration-1000" />
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl animate-pulse-slow" />

            <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="space-y-3">
                    <p className="text-green-200 text-sm uppercase tracking-[0.2em] font-medium">{getGreeting()}</p>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                        {user?.full_name || 'Agent'} <span className="inline-block animate-wave">👋</span>
                    </h1>
                    <p className="text-green-100/80 max-w-md">
                        Track performance, manage quotes, and issue policies from one place.
                    </p>

                    <div className="flex gap-2 mt-4 flex-wrap">
                        <HeroPill icon="📋" label={`${totalQuotes} Quotes`} />
                        <HeroPill icon="📄" label={`${policies} Policies`} />
                        <HeroPill icon="💰" label={`KES ${(commissions || 0).toLocaleString()}`} />
                        {growthRate !== 0 && (
                            <HeroPill
                                icon={growthRate > 0 ? '📈' : '📉'}
                                label={`${Math.abs(growthRate)}%`}
                                tone={growthRate > 0 ? 'positive' : 'negative'}
                            />
                        )}
                    </div>
                </div>

                <div className="flex gap-3 flex-wrap">
                    <Link
                        to="/quotes/new"
                        className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 text-white font-semibold py-3 px-6 rounded-xl hover:bg-white hover:text-[#3a663f] transition-all duration-300 shadow-lg group/btn focus:outline-none focus:ring-2 focus:ring-white/60"
                    >
                        <svg className="w-5 h-5 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        New Quote
                    </Link>
                    <Link
                        to="/cargo"
                        className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 text-white font-semibold py-3 px-6 rounded-xl hover:bg-white hover:text-[#3a663f] transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-white/60"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Cargo Certificate
                    </Link>
                </div>
            </div>
        </div>
    );
}

function HeroPill({ icon, label, tone }) {
    const toneClass = tone === 'positive'
        ? 'bg-green-500/30 text-green-100 border-green-400/40'
        : tone === 'negative'
            ? 'bg-red-500/30 text-red-100 border-red-400/40'
            : 'bg-white/15 text-green-100 border-white/10';
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 backdrop-blur-sm rounded-full text-xs font-medium border ${toneClass}`}>
            <span>{icon}</span>
            {label}
        </span>
    );
}

// =================== QUICK ACTION ===================
function QuickAction({ to, icon, label, color = 'green', badge, onClick }) {
    const colors = {
        blue: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
        green: 'from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54]',
        purple: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
        amber: 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700',
        slate: 'from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800',
    };
    const cls = `group relative flex flex-col items-center justify-center gap-2 py-4 rounded-2xl bg-gradient-to-br ${colors[color] || colors.green} text-white shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3a663f]/50`;

    const inner = (
        <>
            <span className="text-2xl group-hover:scale-110 transition-transform">{icon}</span>
            <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
            {badge > 0 && (
                <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#3a663f] text-[10px] font-extrabold flex items-center justify-center shadow-md">
                    {badge}
                </span>
            )}
        </>
    );

    if (onClick) {
        return <button type="button" onClick={onClick} className={cls}>{inner}</button>;
    }
    return <Link to={to} className={cls}>{inner}</Link>;
}

// =================== STAT CARD ===================
function StatCard({ icon, label, value, subValue, gradient, accent, sparkline, trend, prefix = '', suffix = '' }) {
    const animated = useCountUp(typeof value === 'number' ? value : 0);
    const display = typeof value === 'number' ? `${prefix}${animated.toLocaleString()}${suffix}` : value;

    return (
        <div className={`relative group overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}>
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/5 blur-xl" />

            <div className="relative p-6 flex flex-col justify-between h-full">
                <div className="flex items-start justify-between">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm text-2xl group-hover:scale-110 transition-transform">
                        {icon}
                    </div>
                    {typeof trend === 'number' && trend !== 0 && (
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${trend > 0 ? 'bg-green-400/30 text-green-100' : 'bg-red-400/30 text-red-100'
                            }`}>
                            {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}%
                        </span>
                    )}
                </div>

                <div className="space-y-1 mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider opacity-80">{label}</p>
                    <p className="text-3xl font-extrabold tracking-tight tabular-nums">{display}</p>
                    {subValue && <p className="text-xs opacity-70 mt-1">{subValue}</p>}
                </div>

                {sparkline && sparkline.length > 1 && (
                    <div className="mt-4 opacity-80 group-hover:opacity-100 transition-opacity">
                        <MiniSparkline data={sparkline} />
                    </div>
                )}

                <div className="mt-3 h-1 w-12 rounded-full opacity-80 group-hover:w-16 transition-all" style={{ backgroundColor: accent }} />
            </div>
        </div>
    );
}

function MiniSparkline({ data }) {
    const w = 100, h = 24;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const step = w / (data.length - 1);
    const points = data.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(' ');
    const areaPoints = `0,${h} ${points} ${w},${h}`;

    return (
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="overflow-visible">
            <defs>
                <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
            </defs>
            <polygon fill="url(#sparkGrad)" points={areaPoints} />
            <polyline fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
        </svg>
    );
}

// =================== ACTIVITY FEED ===================
function ActivityFeed({ items, loading }) {
    return (
        <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-100 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                    <span className="w-1.5 h-6 bg-[#3a663f] rounded-full" />
                    Activity Timeline
                </h3>
                <span className="text-xs text-gray-400 dark:text-slate-500">Latest 6 events</span>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-16 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <EmptyState
                    message="No activity yet. Create your first quote or certificate!"
                    link="/quotes/new"
                    linkText="Get Started"
                />
            ) : (
                <div className="relative">
                    <div className="absolute left-6 top-2 bottom-2 w-px bg-gradient-to-b from-[#3a663f]/40 via-gray-200 to-transparent dark:from-green-400/30 dark:via-slate-700" />
                    <ul className="space-y-4">
                        {items.map((item) => (
                            <li key={item.id} className="relative pl-14">
                                <Link
                                    to={item.link}
                                    className="block group rounded-2xl p-3 -ml-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                                >
                                    <span className={`absolute left-3 top-5 w-7 h-7 rounded-full flex items-center justify-center text-sm shadow-md ring-4 ring-white dark:ring-slate-800 ${item.color === 'green' ? 'bg-green-500' :
                                            item.color === 'red' ? 'bg-red-500' :
                                                item.color === 'blue' ? 'bg-blue-500' :
                                                    'bg-purple-500'
                                        }`}>
                                        {item.icon}
                                    </span>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-gray-800 dark:text-white truncate">
                                                {item.title}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                                                {item.subtitle} • <span className="font-medium">{item.meta}</span>
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            <span className="text-xs text-gray-400 dark:text-slate-500">
                                                {formatRelativeTime(item.date)}
                                            </span>
                                            {item.status && <Badge status={item.status} />}
                                        </div>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// =================== GOAL RING ===================
function GoalRing({ value = 0, target = 80 }) {
    const pct = Math.min(Math.max(value, 0), 100);
    const radius = 54;
    const circ = 2 * Math.PI * radius;
    const offset = circ - (pct / 100) * circ;
    const met = pct >= target;

    return (
        <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-100 dark:border-slate-700 p-6 text-center">
            <h3 className="text-sm font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                Monthly Goal
            </h3>
            <div className="relative inline-block">
                <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
                    <circle cx="70" cy="70" r={radius} stroke="currentColor" strokeWidth="10" fill="none" className="text-gray-100 dark:text-slate-700" />
                    <circle
                        cx="70" cy="70" r={radius}
                        stroke={met ? '#22c55e' : '#3a663f'}
                        strokeWidth="10" fill="none" strokeLinecap="round"
                        strokeDasharray={circ}
                        strokeDashoffset={offset}
                        className="transition-all duration-1000 ease-out"
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-extrabold text-gray-800 dark:text-white tabular-nums">{pct}%</span>
                    <span className="text-xs text-gray-500 dark:text-slate-400">of {target}%</span>
                </div>
            </div>
            <p className={`mt-4 text-sm font-medium ${met ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'}`}>
                {met ? '🎉 Target achieved!' : `${(target - pct).toFixed(0)}% to go`}
            </p>
        </div>
    );
}

// =================== QUICK STATS ===================
function QuickStats({ certificates, pendingQuotes, policies }) {
    const items = [
        { label: 'Certificates', value: certificates, icon: '📁', color: 'text-purple-600 dark:text-purple-400' },
        { label: 'Pending Quotes', value: pendingQuotes, icon: '⏳', color: 'text-amber-600 dark:text-amber-400' },
        { label: 'Policies', value: policies, icon: '🛡️', color: 'text-[#3a663f] dark:text-green-400' },
    ];

    return (
        <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-100 dark:border-slate-700 p-6">
            <h3 className="text-sm font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                At a Glance
            </h3>
            <div className="space-y-3">
                {items.map((it) => (
                    <div key={it.label} className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
                            <span className="text-lg">{it.icon}</span>
                            {it.label}
                        </span>
                        <span className={`text-lg font-extrabold tabular-nums ${it.color}`}>{it.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// =================== ACTIVITY PANEL ===================
function ActivityPanel({ title, to, loading, isEmpty, emptyMessage, emptyLink, emptyLinkText, children }) {
    return (
        <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                    <span className="w-1.5 h-6 bg-[#3a663f] rounded-full" />
                    {title}
                </h3>
                <Link to={to} className="text-sm text-[#3a663f] dark:text-green-400 hover:underline font-medium flex items-center gap-1">
                    View All
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </Link>
            </div>
            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />)}
                </div>
            ) : isEmpty ? (
                <EmptyState message={emptyMessage} link={emptyLink} linkText={emptyLinkText} />
            ) : (
                <div className="space-y-3">{children}</div>
            )}
        </div>
    );
}

// =================== ITEM ===================
function PolicyItem({ policy }) {
    return (
        <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group">
            <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 dark:text-white truncate">{policy.policy_number}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                    <span className="truncate">{policy.vessel_name || 'N/A'}</span>
                    <span>•</span>
                    <span>KES {Number(policy.sum_insured || 0).toLocaleString()}</span>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <Badge status={policy.payment_status || 'active'} />
                <Link to={`/policies/${policy.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-sm text-[#3a663f] dark:text-green-400 hover:underline">
                    View
                </Link>
            </div>
        </div>
    );
}

// =================== EMPTY ===================
function EmptyState({ message, link, linkText }) {
    return (
        <div className="text-center py-8">
            <svg className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-gray-400 dark:text-slate-500 text-sm mb-3">{message}</p>
            {link && (
                <Link to={link} className="inline-flex items-center gap-1.5 text-sm text-[#3a663f] dark:text-green-400 font-medium hover:text-[#2d5232] dark:hover:text-green-300 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {linkText}
                </Link>
            )}
        </div>
    );
}

// =================== ERROR ===================
function ErrorCard({ error, onRetry }) {
    return (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center">
            <svg className="w-12 h-12 text-red-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-bold text-red-700 dark:text-red-400">Unable to Load Dashboard</h3>
            <p className="text-sm text-red-600 dark:text-red-300 mt-2">{error.message || 'An error occurred while loading your data.'}</p>
            <button onClick={onRetry} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#3a663f] text-white rounded-xl hover:bg-[#2d5232] transition-colors">
                Retry
            </button>
        </div>
    );
}

// =================== COMMAND PALETTE ===================
function CommandPalette({ onClose, navigate }) {
    const [q, setQ] = useState('');
    const commands = [
        { icon: '➕', label: 'New Quote', to: '/quotes/new' },
        { icon: '📄', label: 'Cargo Certificate', to: '/cargo' },
        { icon: '📁', label: 'My Certificates', to: '/cargo/certificates' },
        { icon: '🆔', label: 'IDF Insurance Check', to: '/idf-check' },
        { icon: '🛡️', label: 'Policies', to: '/policies' },
        { icon: '📋', label: 'Quotes', to: '/quotes' },
        { icon: '🚢', label: 'Vessel Tracking', to: '/vessel-tracker' },
        { icon: '📊', label: 'Reports', to: '/reports' },
    ];
    const filtered = commands.filter((c) => c.label.toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-32 px-4 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-slideUp"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        autoFocus
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Type a command or search..."
                        className="flex-1 bg-transparent outline-none text-gray-800 dark:text-white placeholder-gray-400"
                    />
                    <kbd className="hidden sm:inline-block text-[10px] px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 rounded font-mono">ESC</kbd>
                </div>
                <div className="max-h-80 overflow-y-auto py-2">
                    {filtered.length === 0 ? (
                        <p className="text-center text-gray-400 text-sm py-8">No matching commands</p>
                    ) : filtered.map((cmd) => (
                        <button
                            key={cmd.to}
                            onClick={() => { navigate(cmd.to); onClose(); }}
                            className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                        >
                            <span className="text-xl">{cmd.icon}</span>
                            <span className="text-sm text-gray-700 dark:text-slate-200">{cmd.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

// =================== TOASTS ===================
function ToastStack({ toasts, onDismiss }) {
    return (
        <div className="fixed bottom-6 right-6 z-50 space-y-3 pointer-events-none">
            {toasts.map((t) => (
                <div
                    key={t.id}
                    className="pointer-events-auto flex items-start gap-3 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 px-5 py-4 min-w-[300px] max-w-md animate-slideInRight"
                >
                    <span className="text-xl">{t.icon}</span>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 dark:text-white text-sm">{t.title}</p>
                        {t.message && <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{t.message}</p>}
                    </div>
                    <button
                        onClick={() => onDismiss(t.id)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            ))}
        </div>
    );
}

// =================== SKELETON ===================
function SkeletonLoader() {
    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8 animate-pulse">
            <div className="h-12 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
            <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-3xl" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-gray-100 dark:bg-slate-700 rounded-2xl" />)}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[1, 2, 3, 4].map((i) => <div key={i} className="h-40 bg-gray-100 dark:bg-slate-700 rounded-2xl" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 h-96 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                <div className="space-y-6">
                    <div className="h-48 bg-gray-100 dark:bg-slate-700 rounded-3xl" />
                    <div className="h-32 bg-gray-100 dark:bg-slate-700 rounded-3xl" />
                </div>
            </div>
        </div>
    );
}